// Angular
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule, registerLocaleData, LocationStrategy, HashLocationStrategy } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClientJsonpModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import localePtExtra from '@angular/common/locales/extra/pt';
import { APP_INITIALIZER, NgModule, Optional, SkipSelf, LOCALE_ID } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ErrorHandler } from '@angular/core';

import { ModalDismissedError, usarSandboxMinificadoDoPdfViewer } from 'src/app/shared/utils';

// CoreUI
import {
  AvatarModule,
  BadgeModule,
  BreadcrumbModule,
  ButtonGroupModule,
  ButtonModule,
  CardModule,
  DropdownModule,
  FooterModule,
  FormModule,
  GridModule,
  HeaderModule,
  ListGroupModule,
  ModalModule,
  NavModule,
  ProgressModule,
  SharedModule,
  SidebarModule,
  TabsModule,
  UtilitiesModule,
} from '@coreui/angular';

import { IconModule, IconSetService } from '@coreui/icons-angular';
import { iconSubset } from '../icons/icon-subset';

// Sentry
import * as Sentry from '@sentry/angular';

// Elastic APM
// import { ApmModule } from '@elastic/apm-rum-angular';
// import { ApmErrorHandler } from '@elastic/apm-rum-angular';

// Firebase
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireDatabaseModule } from '@angular/fire/compat/database';

// 3rd party components
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ToastrModule } from 'ngx-toastr';
import { IsLoadingPipeModule } from '@service-work/is-loading';
import { QrCodeModule } from 'ng-qrcode';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from "ng-recaptcha";
import { NgxSpinnerModule } from "ngx-spinner";

import {
  PERFECT_SCROLLBAR_CONFIG,
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule,
} from 'ngx-perfect-scrollbar';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true
};

// Guards
import { AuthGuard, UnidadeGuard, PermissionGuard, StaffGuard } from 'src/app/core/guards';

import { environment } from 'src/environments/environment';

// Services
import {
  AuthService, AuthApiService, CrossDomainStorageService, BroadcasterService, InactiveCheckService,
  NavService, PromptUpdateService
} from 'src/app/core/services';

// Views
import {
  P404Component, P500Component, LoginComponent,
  OrganizationsComponent, RegisterComponent,
  ChangePasswordComponent, JoinComponent,
  AppRegisterComponent,
  ForgotPasswordComponent
} from 'src/app/core/views';

import { AuthInterceptor } from 'src/app/core/interceptor';
import { PermissionService } from 'src/app/shared/services';

registerLocaleData(localePt);

// O build só publica os assets `*.min.js` do pdf.js; a lib esquece de apontar
// o sandbox para eles. Ver o util para a explicação completa.
usarSandboxMinificadoDoPdfViewer();

/**
 * Sorteia se esta aba grava Replay. O resultado fica no `sessionStorage`
 * para valer na aba inteira — sem isso, cada recarga refaria o sorteio.
 * Na aba sorteada, descarta uma sessão de Replay salva em modo `session`
 * (gravação contínua) por um build anterior: o SDK a retomaria com o
 * modo antigo, apesar de `replaysSessionSampleRate: 0`.
 */
function sorteiaReplay(taxa: number): boolean {
  try {
    const CHAVE = 'ladderReplaySorteado';
    let sorteado = sessionStorage.getItem(CHAVE);
    if (sorteado === null) {
      sorteado = Math.random() < taxa ? '1' : '0';
      sessionStorage.setItem(CHAVE, sorteado);
    }
    if (sorteado !== '1') {
      return false;
    }
    const salva = JSON.parse(
      sessionStorage.getItem('sentryReplaySession') || 'null',
    );
    if (salva && salva.sampled === 'session') {
      sessionStorage.removeItem('sentryReplaySession');
    }
    return true;
  } catch {
    // `sessionStorage` indisponível (modo privado, bloqueio): sem Replay.
    return false;
  }
}

if (environment.production) {
  Sentry.init({
    dsn: environment.bugTrackingUrl,
    debug: false,
    release: `${environment.prefix}@${environment.version}`,
    environment: environment.qa ? 'qa' : 'prod',

    // O Replay só é instalado numa fração das sessões (ver
    // REPLAY_SAMPLE_RATE abaixo). Onde estiver instalado, grava só para
    // anexar ao erro: nada de gravação contínua enviada ao Sentry.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      // new BrowserTracing({
      //   tracePropagationTargets: ['localhost', environment.urlAPI],
      //   routingInstrumentation: Sentry.routingInstrumentation,
      // }),
      Sentry.browserProfilingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
    ],

    tracePropagationTargets: ['localhost', environment.urlAPI],

    // Filtra warnings/erros que não são bugs reais (ruído no Sentry):
    // - PDF.js viewer: avisos de tradução faltando (assets/viewer-*.es5)
    //   + erro interno OverlayManager (race ao abrir overlay)
    // - Google Maps SDK: re-registro de custom elements + deprecations
    // - moment.js: aviso de formato não-RFC2822
    // - Extensões de browser interferindo: read-only FontFace, embed PDF
    // - Cloudflare Bot Management injeta script inline antes de </body>
    //   que cria iframe e acessa contentWindow.document; em certos
    //   browsers o contentWindow é null no DOMContentLoaded e dispara
    //   TypeError. Não é nosso código (script injetado pelo edge).
    // - prosemirror-tables 1.8.5: bug ao apertar Backspace dentro de
    //   tabela faz normalizeSelection calcular start+1 com start
    //   undefined => RangeError "Position NaN out of range".
    //   Erro async (MutationObserver), não quebra o editor.
    // - Firebase auth/network-request-failed: erro transiente de rede
    //   do próprio usuário. Já tratado em auth-api.service com toast
    //   amigável; reportar ao Sentry só gera ruído não-acionável.
    // (Cancelamento de modal / ModalDismissedError é filtrado no beforeSend,
    //  com validação de tipo/padrão — ver GESTOR-CLIENT-NM abaixo.)
    ignoreErrors: [
      /^#\w+ is undefined\.?$/,
      /^Translation for the key #\w+ is missing\.?$/,
      /^The overlay does not exist\.?$/,
      /^Element with name "gmp-/,
      /google\.maps\.Marker is deprecated/,
      /Drawing library functionality.*deprecated/i,
      /loading=async/,
      /^Deprecation warning: value provided is not in a recognized RFC2822/,
      /Cannot assign to read only property 'FontFace'/,
      /^This browser does not support embedded PDFs$/,
      /Cannot read properties of null \(reading 'document'\)/i,
      /Cannot read properties of null \(reading 'contentDocument'\)/i,
      /a\.contentWindow is null/i,
      /Position NaN out of range/,
      /ng_jsonp_callback_\d+ is not a function/,
      /auth\/network-request-failed/,
      // pdf.js: cancelamentos benignos de teardown (usuário fecha/navega antes
      // do PDF carregar). Escapam do handler do pdf-viewer quando disparam após
      // o componente ser destruído, ou vêm do console.error do próprio viewer.
      // GESTOR-CLIENT-T5/T6/TQ. (T4 é tratado no onPdfLoadingFailed.)
      /Transport destroyed/,
      /Invalid page request/,
      // ngx-extended-pdf-viewer: warning não-fatal de localização. O asset
      // existe e é deployado; o 404 é transitório (janela de deploy/SW) e o
      // viewer cai em fallback (inglês). Não é erro. GESTOR-CLIENT-RQ.
      /viewer\.properties not found/,
      // pdf.js PDFLinkService: link/anotação do PDF com destino que não é um
      // named destination válido (ex.: hyperlink relativo no conteúdo). Só
      // loga e não navega — não quebra nada. GESTOR-CLIENT-TJ.
      /is not a valid destination array/,
      // Extensão de navegador "cently" (cashback/cupons) falhando ao injetar
      // seu script na página. Não é nosso código. GESTOR-CLIENT-TR.
      /\[cently:/,
      // Falha transitória de rede ao carregar o Service Worker (ngsw). Não é
      // acionável no código; o app funciona sem o SW nesse load. GESTOR-CLIENT-TT.
      /ngsw-worker\.js load failed/,
      // Mesma causa da TT, outras duas formas que o browser dá ao mesmo
      // evento: o fetch do ngsw-worker.js falha e o registro/instalação do
      // Service Worker aborta. GESTOR-CLIENT-VC / GESTOR-CLIENT-V5.
      /ServiceWorker script at .*ngsw-worker\.js.*during installation/,
      /Failed to register a ServiceWorker.*ngsw-worker\.js/,
      // pdf.js: o viewer avisa quando uma segunda instância é aberta antes de
      // a anterior ser destruída (modal reaberto rápido). É aviso de
      // diagnóstico da lib, o viewer segue funcionando. GESTOR-CLIENT-SZ.
      /trying to open two instances of the PDF viewer/,
      // pdf.js: impressão pedida antes de o print service terminar de
      // inicializar (usuário aperta Ctrl+P durante o carregamento). A própria
      // lib aborta a impressão e segue. GESTOR-CLIENT-V4.
      /^Expected print service to be initialized\.?$/,
      // pdf.js: o viewer não valida assinatura digital -- é uma limitação
      // conhecida da lib, anunciada como warning em todo PDF assinado. Já
      // mostramos o aviso ao usuário no template. GESTOR-CLIENT-V9.
      /^Warning: Digital signatures validation is not supported/,
      // pdf.js l10n: locale sem arquivo de tradução no bundle do viewer. Cai
      // no fallback (inglês) e não quebra nada -- mesma família do
      // "viewer.properties not found" acima. GESTOR-CLIENT-RR.
      /^"[a-z]{2}(-[a-z]{2})?" resource not found$/i,
      // NG0908 (MISSING_ZONEJS) NÃO é filtrado de propósito: significa app que
      // não inicia, e agora os entrypoints têm a recuperação de
      // src/main/shared/ng0908-recovery.ts. O que chega aqui é só o que ela
      // NÃO conseguiu resolver -- a tentativa bem-sucedida loga em `info`,
      // fora do alcance do captureConsoleIntegration. GESTOR-CLIENT-PK.
      //
      // HTTP status 0: a requisição não chegou ao servidor (usuário navegou
      // durante o request, rede caiu, conexão abortada). Não é erro de
      // aplicação. GESTOR-CLIENT-ST.
      /Http failure response for .*: 0 Unknown Error/,
      // Mesmo caso, já normalizado por descreverErroHttp() antes de chegar ao
      // console -- sem esta entrada o status 0 seria filtrado quando cru e
      // reportado quando tratado.
      /: sem conexão com o servidor$/,
    ],

    // Fallback resiliente: identifica o script inline da Cloudflare
    // pelo cdn-cgi/challenge-platform, pelo trecho de código injetado
    // (a.contentDocument||a.contentWindow.document) ou pela função `c`
    // registrada em HTMLDocument. Cobre minificações futuras do CF.
    beforeSend(event, hint) {
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      const stackBlob = frames
        .map(f => `${f.filename ?? ''} ${f.function ?? ''} ${f.context_line ?? ''}`)
        .join('\n');

      const isCloudflareChallenge =
        /\/cdn-cgi\/challenge-platform\//i.test(stackBlob) ||
        /a\.contentDocument\s*\|\|\s*a\.contentWindow\.document/i.test(stackBlob) ||
        frames.some(f => f.function === 'c' || f.function === 'HTMLDocument.c');

      // Cancelamento de modal (ModalDismissedError) não é erro (GESTOR-CLIENT-NM).
      // Validação restrita — não descarta um erro real que só contenha a string:
      // (a) instância direta / mesmo name (captura via onunhandledrejection global);
      // (b) rejeição não-tratada re-empacotada pelo zone.js num Error genérico,
      //     casando SÓ o padrão "Uncaught (in promise): ... [modal-dismissed]".
      const orig = hint?.originalException as { name?: string; message?: string } | undefined;
      const value = orig?.message ?? event.exception?.values?.[0]?.value ?? event.message ?? '';
      const isModalDismissed =
        orig instanceof ModalDismissedError ||
        orig?.name === 'ModalDismissedError' ||
        /^Uncaught \(in promise\):.*\[modal-dismissed\]/.test(value);

      // Firebase auth: erros de INPUT do usuário (e-mail inexistente, senha
      // errada) não são bugs — o app já mostra mensagem amigável. Filtra o
      // ruído (inclusive a rejeição que vaza como unhandled via @angular/fire,
      // GESTOR-CLIENT-Q4), MAS preserva os alertas de segurança deliberados
      // (brute-force/rate-limit capturados em auth.service com a tag
      // security_alert, ex.: GESTOR-CLIENT-TV).
      const isSecurityAlert = !!event.tags?.['security_alert'];
      const isBenignAuthUserError =
        /auth\/(user-not-found|wrong-password|invalid-email|invalid-credential)/.test(value);

      return isCloudflareChallenge ||
        isModalDismissed ||
        (isBenignAuthUserError && !isSecurityAlert)
        ? null
        : event;
    },

    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // results in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 0.2,

    // initialScope: {
    //   tags: { 'url-base': environment.urlBase }
    // },

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.5,
  });

  // Com `replaysOnErrorSampleRate > 0`, o SDK põe toda sessão em modo
  // buffer — grava o DOM o tempo inteiro (~2% de CPU com a aba parada) e
  // a taxa só decide se a gravação é enviada quando há erro. Para que a
  // maioria das sessões não pague esse custo, o sorteio acontece aqui:
  // fora dele, o Replay nem é instalado.
  if (sorteiaReplay(0.1)) {
    Sentry.addIntegration(
      // Máscara ligada: a gravação vai para o Sentry e a tela traz dados
      // pessoais (nome, CPF, dados bancários).
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    );
  }
}

const COREUI_MODULES = [
  AvatarModule,
  BadgeModule,
  BreadcrumbModule,
  ButtonGroupModule,
  ButtonModule,
  CardModule,
  DropdownModule,
  FooterModule,
  FormModule,
  GridModule,
  HeaderModule,
  ListGroupModule,
  ModalModule,
  NavModule,
  ProgressModule,
  SharedModule,
  SidebarModule,
  TabsModule,
  UtilitiesModule,
  IconModule,
];

const VIEWS = [
  LoginComponent,
  OrganizationsComponent,
  P404Component,
  P500Component,
  RegisterComponent,
  ChangePasswordComponent,
  JoinComponent,
  AppRegisterComponent,
  ForgotPasswordComponent
];

const GUARDS = [
  AuthGuard,
  UnidadeGuard,
  PermissionGuard,
  StaffGuard
];

const SERVICES = [
  NavService,
  AuthService,
  AuthApiService,
  BroadcasterService,
  CrossDomainStorageService,
  InactiveCheckService,
  PermissionService,
  PromptUpdateService
];

// Register the locale data
registerLocaleData(localePt, 'pt-BR', localePtExtra);

@NgModule({
  imports: [
    // ApmModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,

    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    AngularFireDatabaseModule,

    ...COREUI_MODULES,
    ToastrModule.forRoot({
      timeOut: 10000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
      enableHtml: true
    }),
    BsDropdownModule.forRoot(),
    IsLoadingPipeModule,
    QrCodeModule,
    RecaptchaV3Module,
    NgxSpinnerModule
  ],
  declarations: [
    ...VIEWS
  ],
  exports: [
    BrowserAnimationsModule,
    BrowserModule,
    BsDropdownModule,
    FormsModule,
    HttpClientModule,
    HttpClientJsonpModule,
    PerfectScrollbarModule,
    IsLoadingPipeModule,

    AngularFireModule,
    AngularFireAuthModule,
    AngularFireDatabaseModule,
    NgxSpinnerModule,

    ...COREUI_MODULES,

    ...VIEWS,
  ],
  providers: [
    // { provide: ErrorHandler, useClass: ApmErrorHandler },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaKey },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        dialogOptions: {
          lang: 'pt-BR',
          title: 'Parece que houve um problema.',
          subtitle: 'Nosso time foi notificado. ',
          subtitle2: 'Se você quiser ajudar, conte-nos o que aconteceu abaixo:',
          labelName: 'Seu Nome',
          labelEmail: 'E-mail',
          labelComments: 'O que aconteceu?',
          labelClose: 'Fechar',
          labelSubmit: 'Enviar',
          // errorGeneric: 'Eu cliquei no botão "X" e aconteceu o problema.',
          // errorFormEntry: 'Eu cliquei no botão "X" e aconteceu o problema.',
          successMessage: 'Sua mensagem foi enviada, iremos verificar o que aconteceu. Obrigado!',
        }
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    IconSetService,
    Title,
    ...GUARDS,
    ...SERVICES
  ],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule, private iconService: IconSetService) {
    if (parentModule) {
      throw new Error('CoreModule já foi carregado. Você deveria importar este módulo apenas em AppModule.');
    }

    iconService.icons = { ...iconSubset };
  }
}
